import { STICKER_WIDTH, STICKER_HEIGHT } from './stickerSpec';

/**
 * How the user framed the background photo: zoomed over the cover fit, then
 * moved. Pan is in sticker pixels from the centred position, so a drag on the
 * preview converts with nothing but the preview's scale.
 */
export interface Framing {
  panX: number;
  panY: number;
  zoom: number;
}

export const DEFAULT_FRAMING: Framing = { panX: 0, panY: 0, zoom: 1 };
export const MAX_ZOOM = 3;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

function scaledSize(imageWidth: number, imageHeight: number, zoom: number) {
  const scale = Math.max(STICKER_WIDTH / imageWidth, STICKER_HEIGHT / imageHeight) * zoom;
  return { w: imageWidth * scale, h: imageHeight * scale };
}

/**
 * Pulls a framing back inside what the photo can cover: zoom within 1..MAX_ZOOM
 * and no pan that would leave an edge of the sticker bare. Stored clamped, so a
 * drag past the edge doesn't build up slack the user has to drag back through.
 */
export function clampFraming(imageWidth: number, imageHeight: number, framing: Framing): Framing {
  const zoom = clamp(framing.zoom, 1, MAX_ZOOM);
  const { w, h } = scaledSize(imageWidth, imageHeight, zoom);
  const maxX = (w - STICKER_WIDTH) / 2;
  const maxY = (h - STICKER_HEIGHT) / 2;
  return { zoom, panX: clamp(framing.panX, -maxX, maxX), panY: clamp(framing.panY, -maxY, maxY) };
}

/** Where the photo is drawn on the 1080x1920 sticker. */
export function coverRect(imageWidth: number, imageHeight: number, framing: Framing) {
  const f = clampFraming(imageWidth, imageHeight, framing);
  const { w, h } = scaledSize(imageWidth, imageHeight, f.zoom);
  return { x: (STICKER_WIDTH - w) / 2 + f.panX, y: (STICKER_HEIGHT - h) / 2 + f.panY, w, h };
}
